import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import type { LoadingStage } from '@/contexts/AuthContext';
import type { ProcessedBranding } from '@/types/tenant';

type NonNullStage = Exclude<LoadingStage, null>;

interface StageDef {
  key: NonNullStage;
  label: string;
  target: number;
}

const STAGES: StageDef[] = [
  { key: 'authenticating',      label: 'Autenticando',           target: 25 },
  { key: 'loading-profile',     label: 'Carregando perfil',      target: 50 },
  { key: 'loading-permissions', label: 'Verificando permissões', target: 75 },
  { key: 'validating',          label: 'Finalizando',            target: 95 },
  { key: 'complete',            label: 'Pronto!',                target: 100 },
];

interface LoadingProgressProps {
  stage: NonNullStage;
  branding: ProcessedBranding;
}

type StepState = 'pending' | 'active' | 'done';

/** Extract a solid hex from a tenant primaryColor that may be a CSS gradient.
 *  Used where `color` (vs `background`) must resolve — gradients can't be a color. */
function solidFromPrimary(color: string | null | undefined, fallback = '#154680'): string {
  if (!color) return fallback;
  if (color.trim().startsWith('#')) return color;
  const hex = color.match(/#[0-9a-fA-F]{3,8}/);
  return hex ? hex[0] : fallback;
}

export default function LoadingProgress({ stage, branding }: LoadingProgressProps) {
  const stageIdx = STAGES.findIndex((s) => s.key === stage);
  const target = STAGES[stageIdx]?.target ?? 0;
  const tenantSolid = solidFromPrimary(branding.primaryColor);

  const [progress, setProgress] = useState(5);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((prev) => {
        if (prev >= target) return prev;
        const diff = target - prev;
        const speed = Math.max(0.4, diff * 0.06);
        return Math.min(prev + speed, target);
      });
    }, 25);
    return () => clearInterval(id);
  }, [target]);

  // Show 4 visible steps; the 5th ('complete') collapses as "all done".
  const visible = STAGES.slice(0, 4);
  const visibleIdx = Math.min(stageIdx, 4);
  const stateFor = (i: number): StepState => {
    if (stageIdx >= 4) return 'done';
    if (i < visibleIdx) return 'done';
    if (i === visibleIdx) return 'active';
    return 'pending';
  };

  return (
    <>
      {branding.logoUrl && (
        <div className="auth-card-logo-wrapper">
          <img
            src={branding.logoUrl}
            alt={`${branding.tenantName} Logo`}
            className="auth-card-logo"
          />
        </div>
      )}

      <div className="auth-progress-row">
        <div className="auth-progress-track">
          <div
            className="auth-progress-fill"
            style={{
              width: `${progress}%`,
              background: branding.primaryColor,
            }}
          >
            <div className="auth-progress-shimmer" aria-hidden="true" />
          </div>
        </div>
        <span className="auth-progress-percent">{Math.round(progress)}%</span>
      </div>

      <ol
        className="auth-stepper"
        style={{ '--tenant-solid': tenantSolid } as React.CSSProperties}
      >
        {visible.map((s, i) => {
          const state = stateFor(i);
          const isLast = i === visible.length - 1;
          return (
            <li key={s.key} className={`auth-step auth-step--${state}`}>
              {!isLast && (
                <span className="auth-step-connector" aria-hidden="true" />
              )}
              <span className="auth-step-circle" aria-hidden="true">
                {state === 'done' ? (
                  <Check size={14} strokeWidth={2.5} />
                ) : (
                  <span className="auth-step-number">{i + 1}</span>
                )}
                {state === 'active' && (
                  <span className="auth-step-pulse" aria-hidden="true" />
                )}
              </span>
              <div className="auth-step-text">
                <div className="auth-step-label">{s.label}</div>
                {state === 'active' && (
                  <div className="auth-step-status auth-step-status--active">
                    em andamento…
                  </div>
                )}
                {state === 'done' && (
                  <div className="auth-step-status auth-step-status--done">
                    concluído
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}
